import React, { useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { useRouter, Link } from '../../router/Router';
import { isAdminUser } from '../../auth/RouteGuards';
import {
  Bus,
  ShieldCheck,
  Clock,
  MapPin,
  Calendar,
  CheckCircle2,
  ChevronDown,
  ArrowRight,
  Phone,
  Sparkles,
  Armchair,
  CreditCard,
  QrCode,
  Users,
  Zap,
  LayoutDashboard,
  X,
} from 'lucide-react';
import './LandingPage.css';



export const LandingPage: React.FC = () => {
  const { authState, user, logout } = useAuth();
  const { navigate } = useRouter();

  // Mobile Navigation Drawer
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);

  const handleStartBooking = () => {
    if (authState === 'AUTHENTICATED') {
      navigate('/student');
    } else {
      navigate('/register');
    }
  };

  const faqs = [
    {
      q: 'ما هي مواعيد انطلاق الباصات صباحاً من الإسكندرية؟',
      a: 'تنطلق باصات OTO STUDENTS في مواعيد صباحية منتظمة تبدأ من الساعة 5:30 صباحاً حتى 7:15 صباحاً بحسب وجهة الجامعة، مما يضمن وصول الطلاب قبل موعد المحاضرات الأولى بـ 30 دقيقة على الأقل.',
    },
    {
      q: 'كيف أضمن مقعدي في أسطول باصات Otobeesy؟',
      a: 'عند الحجز عبر OTO STUDENTS، تختار رقم مقعدك الفردي المريح بدقة. المقعد يُحجز باسمك ولا يُسمح بركوب أي شخص غير مسجل في الكشف أو الوقوف داخل الأتوبيس نهائياً لضمان أعلى مستويات الراحة والأمان.',
    },
    {
      q: 'ما هي وسائل الدفع المعتمدة؟',
      a: 'يمكنك السداد الفوري عبر تطبيق إنستاباي (InstaPay) لأي بنك مصري، أو عبر المحافظ الإلكترونية الذكية (فودافون كاش، أورنج، اتصالات، وي باي)، أو الدفع نقداً بمقر خدمات الطلاب.',
    },
    {
      q: 'هل يمكنني حجز رحلات لعدة أيام متفرقة أو الشهر كاملاً؟',
      a: 'نعم! المنظومة تدعم حجز رحلة يوم واحد، أو تحديد أيام دراستك في الأسبوع (مثلاً: أحد وثلاثاء وخميس) لشهر كامل بضغطة زر واحدة مع خصم الحجز المتعدد.',
    },
    {
      q: 'أين توجد نقاط التجمع والركوب في الإسكندرية؟',
      a: 'لدينا 4 نقاط تجمع رئيسية تغطي كافة مناطق الإسكندرية: محطة سيدي جابر، محطة مصر، محرم بك، وسبورتنج على طريق الحرية، مع إمكانية التنسيق للنزول عند بوابات الكليات مباشرة.',
    },
  ];

  return (
    <div className="oto-landing-wrapper">
      {/* Top Navbar */}
      <header className="oto-navbar">
        <div className="oto-container">
          <div className="oto-navbar-inner">
            {/* Brand Logo & Name */}
            <Link to="/" className="oto-brand-group">
              <div className="oto-logo-badge">
                <img src="/oto-logo.jpg" alt="OTO STUDENTS Logo" />
              </div>
              <div className="oto-brand-text-block">
                <span className="oto-brand-name">
                  OTO <span>STUDENTS</span>
                </span>
                <span className="oto-brand-slogan">سفرك للجامعة أسهل، أسرع، وأأمن</span>
              </div>
            </Link>

            {/* Navigation Links */}
            <nav className="oto-nav-links">
              <a href="#fleet">أسطول أتوبيسات Otobeesy</a>
              <a href="#features">المميزات</a>
              <a href="#how-it-works">كيف تحجز؟</a>
              <a href="#faq">الأسئلة الشائعة</a>
            </nav>

            {/* User Actions */}
            <div className="oto-nav-actions">
              {authState === 'AUTHENTICATED' ? (
                <>
                  {isAdminUser(user) ? (
                    <Link to="/admin" className="btn-oto-book-now" style={{ background: 'linear-gradient(135deg, #e11d48, #be123c)' }}>
                      <LayoutDashboard size={15} />
                      <span className="btn-text-full">لوحة الإدارة</span>
                      <span className="btn-text-short">الإدارة</span>
                    </Link>
                  ) : (
                    <Link to="/student" className="btn-oto-book-now">
                      <Users size={15} />
                      <span className="btn-text-full">لوحة الطالب</span>
                      <span className="btn-text-short">حسابي</span>
                    </Link>
                  )}
                  <button onClick={() => logout()} className="btn-oto-login">
                    خروج
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn-oto-login">
                    <span className="btn-text-full">تسجيل الدخول</span>
                    <span className="btn-text-short">دخول</span>
                  </Link>
                  <Link to="/register" className="btn-oto-book-now">
                    <Sparkles size={15} />
                    <span className="btn-text-full">إنشاء حساب طالب</span>
                    <span className="btn-text-short">حساب جديد</span>
                  </Link>
                </>
              )}

              {/* Mobile Drawer Toggle */}
              <button
                type="button"
                className="oto-mobile-nav-toggle"
                onClick={() => setMobileNavOpen(!mobileNavOpen)}
                aria-label="القائمة الرئيسية"
              >
                {mobileNavOpen ? (
                  <X size={20} />
                ) : (
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Slide-down Drawer */}
        {mobileNavOpen && (
          <div className="oto-mobile-nav-drawer">
            <div className="oto-mobile-nav-links">
              <a href="#fleet" onClick={() => setMobileNavOpen(false)}>
                <span>أسطول أتوبيسات Otobeesy</span>
              </a>
              <a href="#features" onClick={() => setMobileNavOpen(false)}>
                <span>مميزات الخدمة والأمان</span>
              </a>
              <a href="#how-it-works" onClick={() => setMobileNavOpen(false)}>
                <span>خطوات الحجز السريع</span>
              </a>
              <a href="#faq" onClick={() => setMobileNavOpen(false)}>
                <span>الأسئلة الشائعة</span>
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="oto-hero-section">
        <div className="oto-hero-glow-bg"></div>

        <div className="oto-container">
          <div className="oto-hero-grid">
            {/* Hero Left: Text & Animated Feature Showcase Panel (Browsing only) */}
            <div className="oto-hero-content">
              <div className="oto-hero-badge">
                <span className="oto-pulse-dot"></span>
                <span>المنظومة الأولى لنقل طلاب الإسكندرية إلى كافة الجامعات المصرية</span>
              </div>

              <h1 className="oto-hero-title">
                سفرك اليومي لجامعتك..
                <br />
                بأرقى أسطول أتوبيسات مع{' '}
                <span className="brand-gradient-text">OTOBEESY</span>
              </h1>

              <p className="oto-hero-description">
                تخلص تماماً من عناء ومعاناة المواصلات اليومية. رحلات مباشرة بمقاعد فردية مريحة
                في <strong>أسطول أتوبيسات سياحية فاخرة ومكيفة</strong> مجهزة بشواحن وواي فاي، تنطلق يومياً من
                الإسكندرية وتصلك مباشرة لباب كليتك وتعود بك سالماً.
              </p>

              {/* Animated Feature Showcase Panel (Read-only browsing, zero user hassle) */}
              <div className="oto-showcase-panel">
                <div className="showcase-panel-header">
                  <div className="showcase-status-indicator">
                    <span className="showcase-status-dot"></span>
                    <span className="font-bold text-white text-xs">رحلات يومية منتظمة ومواعيد دقيقة 100%</span>
                  </div>
                  <span className="text-[11px] text-red-400 font-bold bg-red-950/60 border border-red-800/50 px-2.5 py-0.5 rounded-full">
                    أسطول Otobeesy الفاخر
                  </span>
                </div>

                <div className="showcase-tiles-grid">
                  <div className="showcase-tile">
                    <div className="showcase-tile-icon">
                      <Bus size={20} />
                    </div>
                    <div>
                      <h4 className="showcase-tile-title">أتوبيسات سياحية فاخرة</h4>
                      <p className="showcase-tile-desc">أسطول سياحي حديث ومكيف بمقاعد وثيره وشواحن وواي فاي</p>
                    </div>
                  </div>

                  <div className="showcase-tile">
                    <div className="showcase-tile-icon">
                      <Clock size={20} />
                    </div>
                    <div>
                      <h4 className="showcase-tile-title">مواعيد صباحية منضبطة</h4>
                      <p className="showcase-tile-desc">وصول قبل بدء المحاضرات بـ 30 دقيقة بدون أي تأخير</p>
                    </div>
                  </div>

                  <div className="showcase-tile">
                    <div className="showcase-tile-icon">
                      <Armchair size={20} />
                    </div>
                    <div>
                      <h4 className="showcase-tile-title">مقعد فردي مريح</h4>
                      <p className="showcase-tile-desc">حجز رسمي باسم الطالب بدون أي وقوف أو تكدس</p>
                    </div>
                  </div>

                  <div className="showcase-tile">
                    <div className="showcase-tile-icon">
                      <ShieldCheck size={20} />
                    </div>
                    <div>
                      <h4 className="showcase-tile-title">أمان كامل وتتبع GPS</h4>
                      <p className="showcase-tile-desc">سائقون محترفون وتتبع لحظي لمسار الرحلة كاملة</p>
                    </div>
                  </div>
                </div>

                <div className="showcase-actions-bar">
                  <button onClick={handleStartBooking} className="btn-showcase-primary" style={{ cursor: 'pointer', border: 'none' }}>
                    <Sparkles size={17} />
                    <span>احجز مقعدك الآن</span>
                  </button>

                  <a href="#fleet" className="btn-showcase-secondary">
                    <Bus size={17} />
                    <span>مواصفات أسطول الأتوبيسات</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Hero Right: High-Res Red Bus Image & Floating Stats */}
            <div className="oto-hero-visual">
              <div className="hero-main-image-wrapper">
                <img
                  src="/images/otobeesy_bus.jpg"
                  alt="أسطول أتوبيسات Otobeesy الفاخرة للطلاب"
                  className="hero-main-img"
                />
                <div className="hero-img-overlay-glow"></div>
              </div>

              {/* Floating Stat 1 */}
              <div className="hero-floating-stat-1">
                <div className="stat-icon-glow">
                  <Users size={20} />
                </div>
                <div>
                  <div className="stat-num">+15,000</div>
                  <div className="stat-sub">طالب وطالبة يسافرون بانتظام</div>
                </div>
              </div>

              {/* Floating Stat 2 */}
              <div className="hero-floating-stat-2">
                <div className="stat-icon-glow">
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <div className="stat-num">100% أمان وراحة</div>
                  <div className="stat-sub">أسطول Otobeesy الفاخر</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Route Marquee / Ticker */}
      <section className="oto-ticker-section">
        <div className="oto-ticker-track">
          <div className="oto-ticker-item">
            <span className="ticker-dot"></span>
            <span>أسطول أتوبيسات Otobeesy الفاخر بمقاعد مريحة ومكيفة للطلاب</span>
          </div>
          <div className="oto-ticker-item">
            <span className="ticker-dot"></span>
            <span>انضباط تام في المواعيد ووصول مباشر لبوابات الكليات</span>
          </div>
          <div className="oto-ticker-item">
            <span className="ticker-dot"></span>
            <span>شواحن هواتف متنقلة وواي فاي فائق السرعة طوال الطريق</span>
          </div>
          <div className="oto-ticker-item">
            <span className="ticker-dot"></span>
            <span>تتبع مسار مباشر عبر GPS وأعلى معايير السلامة والأمان</span>
          </div>
          <div className="oto-ticker-item">
            <span className="ticker-dot"></span>
            <span>سداد فوري وسهل عبر InstaPay ومحافظ فودافون كاش وبطاقات بنكية</span>
          </div>
        </div>
      </section>

      {/* Fleet Showcase Section */}
      <section className="oto-section" id="fleet">
        <div className="oto-container">
          <div className="oto-section-header">
            <span className="oto-section-tag">الأسطول الفاخر</span>
            <h2 className="oto-section-title">أسطول أتوبيسات Otobeesy مجهز لراحتك التامة</h2>
            <p className="oto-section-subtitle">
              نعتمد حصرياً على أحدث حافلات وأتوبيسات سياحية فاخرة مكيفة مصممة خصيصاً لتوفير
              أعلى معايير الراحة والرفاهية أثناء السفر على الطرق السريعة.
            </p>
          </div>

          <div className="oto-fleet-grid">
            <div className="fleet-image-frame">
              <img src="/images/otobeesy_bus.jpg" alt="أسطول أتوبيسات Otobeesy الفاخرة" />
              <div className="fleet-badge-floating">
                <span>أسطول أتوبيسات Otobeesy الفاخر • مقاعد مريحة ومكيفة • تكييف فائق • شواحن USB</span>
              </div>
            </div>

            <div className="fleet-specs-list">
              <div className="fleet-spec-item">
                <div className="spec-icon">
                  <Armchair size={22} />
                </div>
                <div>
                  <h4 className="spec-title">مقاعد فردية مريحة وواسعة</h4>
                  <p className="spec-desc">
                    مقاعد مريحة مصممة للسفر مع مساند للرأس ومساحة مريحة للأقدام، لكل طالب مقعده
                    المستقل بدون أي ازدحام.
                  </p>
                </div>
              </div>

              <div className="fleet-spec-item">
                <div className="spec-icon">
                  <Zap size={22} />
                </div>
                <div>
                  <h4 className="spec-title">تكييف فائق وشواحن USB لكل مقعد</h4>
                  <p className="spec-desc">
                    تكييف مركزي يعمل بكفاءة طوال الرحلة، بالإضافة إلى منافذ شحن مخصصة لشحن هاتفك أو
                    حاسوبك خلال الطريق.
                  </p>
                </div>
              </div>

              <div className="fleet-spec-item">
                <div className="spec-icon">
                  <ShieldCheck size={22} />
                </div>
                <div>
                  <h4 className="spec-title">أمان كامل وتتبع حي بالـ GPS</h4>
                  <p className="spec-desc">
                    سائقون مرخصون ذوو خبرة تامة بالطرق السريعة مع نظام محدد سرعة إلكتروني وتتبع
                    لحظي لكافة الرحلات.
                  </p>
                </div>
              </div>

              <div className="fleet-spec-item">
                <div className="spec-icon">
                  <Bus size={22} />
                </div>
                <div>
                  <h4 className="spec-title">نظافة وتعقيم دوري للأسطول</h4>
                  <p className="spec-desc">
                    يخضع كل أتوبيس لفحص فني دوري وتنظيف شامل وتعقيم قبل وبعد كل رحلة لضمان أرقى تجربة سفر تليق بطلاب الجامعات.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>



      {/* Why Choose Us Features */}
      <section className="oto-section" id="features">
        <div className="oto-container">
          <div className="oto-section-header">
            <span className="oto-section-tag">مميزات OTO STUDENTS</span>
            <h2 className="oto-section-title">لماذا يختارنا آلاف الطلاب يومياً؟</h2>
            <p className="oto-section-subtitle">
              صممنا المنظومة لتلبي كافة احتياجات الطالب الجامعي: راحة، التزام زمني، وتكنولوجيا حديثة
            </p>
          </div>

          <div className="oto-features-grid">
            <div className="oto-feature-card">
              <div className="feature-icon-wrapper">
                <Armchair size={26} />
              </div>
              <h3 className="feature-title">مقعدك مضمون مسبقاً</h3>
              <p className="feature-desc">
                وداعاً للتدافع في المواقف والانتظار لساعات. مقعدك محجوز برقم ومسجل باسمك في كشف
                السيارة قبل أن تخرج من منزلك.
              </p>
            </div>

            <div className="oto-feature-card">
              <div className="feature-icon-wrapper">
                <MapPin size={26} />
              </div>
              <h3 className="feature-title">نقاط تجمع رئيسية بالإسكندرية</h3>
              <p className="feature-desc">
                مواقع انطلاق مريحة ومألوفة: محطة سيدي جابر، محطة مصر، محرم بك، وسبورتنج على طريق
                الحرية لتكون قريباً دائماً.
              </p>
            </div>

            <div className="oto-feature-card">
              <div className="feature-icon-wrapper">
                <Calendar size={26} />
              </div>
              <h3 className="feature-title">حجز مرن للأيام والأسابيع</h3>
              <p className="feature-desc">
                احجز يوم بيوم أو حدد جدول أيام دراستك للشهر كاملاً بنقرة واحدة، مع إمكانية تعديل
                المواعيد بسهولة.
              </p>
            </div>

            <div className="oto-feature-card">
              <div className="feature-icon-wrapper">
                <QrCode size={26} />
              </div>
              <h3 className="feature-title">تذكرة رقمية فورية مع QR</h3>
              <p className="feature-desc">
                تذكرتك على هاتفك دائماً. بمجرد تأكيد الحجز تحصل على كود صعود إلكتروني يمسحه السائق
                بسرعة وسلاسة.
              </p>
            </div>

            <div className="oto-feature-card">
              <div className="feature-icon-wrapper">
                <CreditCard size={26} />
              </div>
              <h3 className="feature-title">دفع فوري عبر InstaPay والمحافظ</h3>
              <p className="feature-desc">
                طرق سداد مصرية موثوقة: إنستاباي، محافظ فودافون كاش الذكية، أو السداد نقداً بفرع
                خدمات الطلاب خلال 24 ساعة.
              </p>
            </div>

            <div className="oto-feature-card">
              <div className="feature-icon-wrapper">
                <Phone size={26} />
              </div>
              <h3 className="feature-title">دعم فني ومتابعة حية طوال اليوم</h3>
              <p className="feature-desc">
                فريق عمليات ودعم متواجد على الواتساب والهاتف من الفجر وحتى عودة آخر طالب لمنزله
                للرد على أي استفسار.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Campus & Mobile Experience Visual Row */}
      <section className="oto-section bg-[#0B0203]">
        <div className="oto-container">
          <div className="oto-experience-row">
            <div>
              <span className="oto-section-tag">الحياة الجامعية</span>
              <h2 className="oto-section-title mt-2 mb-4">
                ابدأ يومك الجامعي بنشاط وطاقة بدلاً من إرهاق الطريق
              </h2>
              <p className="text-slate-300 leading-relaxed mb-5">
                هدفنا في <strong>OTO STUDENTS</strong> ليس مجرد توصيلك للجامعة، بل تمكينك من حضور
                محاضراتك وأداء امتحاناتك بكامل تركيزك وحيويتك، بدون إجهاد المواصلات العشوائية.
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3 text-slate-200 font-semibold text-sm">
                  <CheckCircle2 size={18} className="text-red-500" />
                  <span>وصول منتظم في مواعيد المحاضرات الصباحية بدقة 100%</span>
                </div>
                <div className="flex items-center gap-3 text-slate-200 font-semibold text-sm">
                  <CheckCircle2 size={18} className="text-red-500" />
                  <span>صحبة جامعية راقية مع زملائك من نفس كليتك وجامعتك</span>
                </div>
                <div className="flex items-center gap-3 text-slate-200 font-semibold text-sm">
                  <CheckCircle2 size={18} className="text-red-500" />
                  <span>راحة وهدوء داخل باص الهايس للمراجعة أو الاسترخاء في الطريق</span>
                </div>
              </div>

              <div className="mt-6">
                <button onClick={handleStartBooking} className="btn-oto-book-now">
                  <span>انضم لآلاف الطلاب الآن</span>
                  <ArrowRight size={16} />
                </button>
              </div>
            </div>

            <div className="experience-img-card">
              <img
                src="/images/students_campus.jpg"
                alt="طلاب الجامعات المصرية مع OTO STUDENTS"
              />
            </div>
          </div>

          <div className="oto-experience-row mt-16">
            <div className="experience-img-card">
              <img src="/images/mobile_ticket.jpg" alt="تطبيق وتذكرة OTO STUDENTS الرقمية" />
            </div>

            <div>
              <span className="oto-section-tag">تكنولوجيا عصرية</span>
              <h2 className="oto-section-title mt-2 mb-4">
                تذكرتك الرقمية وبوابتك الذكية دائماً على هاتفك
              </h2>
              <p className="text-slate-300 leading-relaxed mb-5">
                وفرنا تجربة ويب وموبايل تشبه أحدث تطبيقات النقل العالمية. احجز مقعدك، ارفع إيصال
                التحويل، استلم تذكرتك الرقمية فورا مع QR Code جاهز للمسح.
              </p>

              <div className="bg-white/5 border border-red-500/20 p-4 rounded-xl space-y-2 text-sm text-slate-300">
                <p>
                  📲 <strong>بدون تحميل تطبيقات معقدة:</strong> افتح الموقع من أي هاتف (iPhone أو
                  Android) واستمتع بتجربة تطبيق كاملة وسريعة.
                </p>
                <p>
                  ⚡ <strong>حفظ بياناتك:</strong> تذكرتك وتاريخ رحلاتك وإيصالاتك محفوظة دائماً في
                  حسابك الآمن.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works 4 Steps */}
      <section className="oto-section" id="how-it-works">
        <div className="oto-container">
          <div className="oto-section-header">
            <span className="oto-section-tag">خطوات الحجز</span>
            <h2 className="oto-section-title">رحلتك لجامعتك في 4 خطوات سهلة</h2>
            <p className="oto-section-subtitle">
              عملية حجز سريعة لا تستغرق أكثر من 60 ثانية من هاتفك المحمول
            </p>
          </div>

          <div className="oto-steps-grid">
            <div className="oto-step-card">
              <div className="step-num-pill">1</div>
              <h4 className="step-title">اختر الجامعة واليوم</h4>
              <p className="step-desc">
                حدد جامعتك من قائمة الجامعات المغطاة واختر يوم السفر وميعاد التحرك المناسب لك.
              </p>
            </div>

            <div className="oto-step-card">
              <div className="step-num-pill">2</div>
              <h4 className="step-title">اختر مقعدك في الهايس</h4>
              <p className="step-desc">
                اختر مقعدك المفضل (شباك، ممر، أو أمامي) من خريطة المقاعد الـ 14 التفاعلية.
              </p>
            </div>

            <div className="oto-step-card">
              <div className="step-num-pill">3</div>
              <h4 className="step-title">ادفع بإنستاباي أو المحفظة</h4>
              <p className="step-desc">
                سدد عبر InstaPay أو فودافون كاش وارفع لقطة شاشة التحويل للتأكيد الفوري.
              </p>
            </div>

            <div className="oto-step-card">
              <div className="step-num-pill">4</div>
              <h4 className="step-title">استلم تذكرتك وانطلق</h4>
              <p className="step-desc">
                تظهر تذكرتك الرقمية مع كود الـ QR، تواجد في نقطة التجمع وانطلق بأمان وراحة.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="oto-section bg-[#0B0203]" id="faq">
        <div className="oto-container">
          <div className="oto-section-header">
            <span className="oto-section-tag">الأسئلة الشائعة</span>
            <h2 className="oto-section-title">كل ما تريد معرفته عن السفر معنا</h2>
            <p className="oto-section-subtitle">إجابات وافية على أكثر الأسئلة تكراراً بين الطلاب</p>
          </div>

          <div className="oto-faq-list">
            {faqs.map((faq, idx) => {
              const isOpen = openFaqIndex === idx;
              return (
                <div key={idx} className={`oto-faq-item ${isOpen ? 'open' : ''}`}>
                  <div
                    className="oto-faq-question"
                    onClick={() => setOpenFaqIndex(isOpen ? null : idx)}
                  >
                    <span>{faq.q}</span>
                    <ChevronDown
                      size={18}
                      className={`transition-transform duration-200 ${
                        isOpen ? 'rotate-180 text-red-500' : ''
                      }`}
                    />
                  </div>
                  {isOpen && <div className="oto-faq-answer">{faq.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bottom CTA Banner */}
      <div className="oto-container">
        <div className="oto-cta-banner">
          <div className="cta-banner-content">
            <h2 className="cta-banner-title">جاهز لتجربة سفر جامعية تليق بك وبمستقبلك؟</h2>
            <p className="cta-banner-subtitle">
              احجز مقعدك الآن في رحلات الأسبوع القادم مع OTO STUDENTS ووفر طاقتك ووقتك لدروسك
              وتفوقك.
            </p>
            <button onClick={handleStartBooking} className="btn-cta-banner-action">
              <span>احجز مقعدك الفردي الآن</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Global Footer */}
      <footer className="oto-footer">
        <div className="oto-container">
          <div className="oto-footer-grid">
            <div className="footer-brand-col">
              <div className="oto-brand-group">
                <div className="oto-logo-badge">
                  <img src="/oto-logo.jpg" alt="OTO STUDENTS" />
                </div>
                <div className="oto-brand-text-block">
                  <span className="oto-brand-name">
                    OTO <span>STUDENTS</span>
                  </span>
                  <span className="oto-brand-slogan">سفرك للجامعة أسهل، أسرع، وأأمن</span>
                </div>
              </div>
              <p className="footer-brand-desc">
                المنصة المتخصصة الأولى لتنظيم رحلات النقل المباشر لطلاب الجامعات المقيمين
                بالإسكندرية إلى كافة المحافظات والجامعات المصرية بأسطول أتوبيسات Otobeesy الفاخرة والمكيفة.
              </p>
            </div>

            <div>
              <h4 className="footer-title">روابط سريعة</h4>
              <ul className="footer-links">
                <li>
                  <a href="#booking">حجز فوري للرحلات</a>
                </li>
                <li>
                  <a href="#universities">الجامعات والمواعيد</a>
                </li>
                <li>
                  <a href="#fleet">أسطول تويوتا هايس</a>
                </li>
                <li>
                  <a href="#features">مميزات السفر معنا</a>
                </li>
                <li>
                  <Link to="/login">تسجيل دخول الطلاب</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-title">الجامعات المغطاة</h4>
              <ul className="footer-links">
                <li>
                  <a href="#universities">جامعة طنطا (سبرباي والطب)</a>
                </li>
                <li>
                  <a href="#universities">جامعة كفر الشيخ</a>
                </li>
                <li>
                  <a href="#universities">جامعة المنصورة</a>
                </li>
                <li>
                  <a href="#universities">جامعات القاهرة وحلوان</a>
                </li>
                <li>
                  <a href="#universities">جامعة الزقازيق والمنوفية</a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="footer-title">تواصل معنا</h4>
              <ul className="footer-links">
                <li className="flex items-center gap-2">
                  <Phone size={15} className="text-red-500" />
                  <span dir="ltr">010 9988 7766</span>
                </li>
                <li>
                  <span>📍 الإسكندرية — سيدي جابر / محطة مصر</span>
                </li>
                <li>
                  <span>⏰ خدمة العملاء: يومياً 5:00 ص - 10:00 م</span>
                </li>
                <li className="mt-2">
                  <span className="text-xs bg-red-950 text-red-300 px-2.5 py-1 rounded border border-red-800">
                    رحلات مرخصة ومؤمنة بالكامل
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom-bar">
            <div>
              جميع الحقوق محفوظة © {new Date().getFullYear()} لمنظومة <strong>OTO STUDENTS</strong>{' '}
              — اوتو ستيودنتس لنقل الطلاب.
            </div>
            <div className="flex items-center gap-4 text-xs text-slate-400">
              <Link to="/login" className="hover:text-red-400">
                بوابة الإدارة (Admin)
              </Link>
              <span>•</span>
              <a href="#faq" className="hover:text-red-400">
                الشروط والأحكام
              </a>
              <span>•</span>
              <a href="#booking" className="hover:text-red-400">
                سياسة الخصوصية
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
